import React, { useEffect, useRef, useState } from "react";
import Button from "./Button";
import { Link } from "react-router-dom";

// Props:
// - open: boolean controls visibility
// - onClose: function called to close
// - title, description, badge, ctaText, imageSrc, listData
const ServicePopUp = ({
  open, // optional controlled prop
  onClose, // optional callback
  title = "Design System",
  description = `The force of knowledge, the impact of creativity, and the pervasiveness of technology come together to shape ideas that truly transform businesses and communities. At Quamm, our marketing and communication consulting is not just about strategies; it is about unlocking potential, designing meaningful connections, and creating long-lasting impressions. We combine analytical depth with creative intuition, blending innovation with proven expertise. By aligning technology with human insight, we help brands find clarity, direction, and resonance in competitive markets. With Quamm, every message is purposeful, every solution is impactful, and every step leads toward sustainable growth and stronger relationships.`,
  badge = "Design",
  ctaText = "Let's Talk",
  imageSrc = "https://i.pinimg.com/736x/45/0a/28/450a289d61677e396ce530405e30d1e7.jpg",
  listData = { title: "", items: [] }, // { title: string, items: array of strings }
  backToPrevious = false,
  id = null,
}) => {
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);
  const isControlled = typeof open === "boolean";
  const [internalOpen, setInternalOpen] = useState(isControlled ? open : false);

  // Keep internal state in sync if switching to controlled mode
  useEffect(() => {
    if (isControlled) {
      setInternalOpen(open);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    // Call external if provided
    if (typeof onClose === "function") onClose();
    // Close internally when uncontrolled
    if (!isControlled) setInternalOpen(false);
  };

  // Close on escape
  useEffect(() => {
    const isOpen = isControlled ? open : internalOpen;
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isControlled, open, internalOpen]);

  // Focus close button on open
  useEffect(() => {
    const isOpen = isControlled ? open : internalOpen;
    if (isOpen && closeBtnRef.current) closeBtnRef.current.focus();
  }, [isControlled, open, internalOpen]);

  const isOpen = isControlled ? open : internalOpen;
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-end p-4 md:p-6 lg:p-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby="service-popup-title"
      ref={dialogRef}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 dark:bg-black/60 bg-white/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal container */}
      <div className="relative w-full max-h-[90vh] overflow-hidden border rounded-2xl dark:border-zinc-700 border-zinc-200">
        <div className="relative w-full bg-white dark:bg-zinc-950 text-black dark:text-white rounded-t-2xl shadow-2xl ring-1 ring-black/10 dark:ring-white/10">
          {/* Header row */}

          {/* Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 max-h-[85vh] overflow-y-auto overflow-x-hidden lg:overflow-hidden rounded-2xl">
            {/* Left: Text */}
            <div className="relative p-6 md:p-8 lg:p-10 flex flex-col min-h-0 order-2 lg:order-1">
              <div className="flex-1 overflow-y-auto">
                <div className="flex items-center justify-between pb-5">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-zinc-100 dark:bg-zinc-800 ring-1 ring-zinc-300 dark:ring-white/10">
                      <span className="inline-block w-2.5 h-2.5 rounded-full bg-pink-500 shadow-[0_0_12px_2px_rgba(236,72,153,0.7)]" />
                    </span>
                    <span className="text-sm md:text-lg text-zinc-600 dark:text-zinc-200 capitalize">
                      {badge}
                    </span>
                  </div>
                  {/* Divider line across */}
                  <div className="hidden md:block flex-1 h-px bg-gradient-to-r from-pink-500 via-pink-500 to-transparent mx-4" />
                </div>

                <h2
                  id="service-popup-title"
                  className="text-3xl md:text-5xl font-extrabold leading-tight text-black dark:text-white"
                >
                  {title}
                </h2>
                <p className="mt-5 text-zinc-700 dark:text-zinc-300 text-base leading-[1.4]">
                  {description}
                </p>

                {/* Service List */}
                {listData && listData.items && listData.items.length > 0 && (
                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-black dark:text-white mb-4">
                      {listData.title}
                    </h3>
                    <ul className="space-y-3">
                      {listData.items.map((item, index) => (
                        <li
                          key={index}
                          className="flex items-start gap-3 group"
                        >
                          {/* Custom bullet point */}
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-pink-500/10 dark:bg-pink-500/20 mt-0.5 flex-shrink-0 group-hover:bg-pink-500/20 dark:group-hover:bg-pink-500/30 transition-colors duration-200">
                            <span className="inline-block w-2 h-2 rounded-full bg-pink-500 group-hover:scale-110 transition-transform duration-200" />
                          </span>
                          {/* List item text */}
                          <span className="text-zinc-700 dark:text-zinc-300 text-base leading-relaxed group-hover:text-zinc-900 dark:group-hover:text-zinc-100 transition-colors duration-200">
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <Link to={"/contact"}>
                  <Button className={"mt-6"}>{ctaText}</Button>
                </Link>
              </div>

              {/* Next Service - Fixed at bottom */}
              <div className="mt-6 pt-4 border-t border-zinc-200 dark:border-zinc-800">
                <Link to={`?service=${backToPrevious ? 1 : id + 1}`}>
                  <div className="lg:text-xl text-zinc-700 dark:text-zinc-300/90 flex items-center gap-3">
                    <span>Next Service</span>
                    <img
                      src="/new-final-data/sections/right-arrow.svg"
                      className="w-10 invert dark:invert-0"
                    />
                  </div>
                </Link>
              </div>

              {/* Gradient overlay */}
              <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-b from-transparent via-transparent to-pink-600/25 pointer-events-none" />
            </div>

            {/* Right: Image + overlay */}
            <div className="relative min-h-[300px] lg:min-h-[400px] max-h-[85vh] order-1 lg:order-2">
              {/* Close at top-right over image */}
              <button
                ref={closeBtnRef}
                onClick={handleClose}
                aria-label="Close"
                className="absolute cursor-pointer top-4 right-4 z-10 text-white/95 hover:text-white flex items-center gap-2 px-3 py-1.5 focus:outline-none"
              >
                <span className="text-base md:text-xl">Close</span>
                <span className="text-xl md:text-4xl leading-none">×</span>
              </button>

              <img
                src={imageSrc}
                alt="Service visual"
                className="w-full h-full object-cover"
              />
              {/* vignette and highlight */}
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white/15 dark:to-black/40" />
              <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full bg-pink-500/40 blur-2xl" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicePopUp;
